/**
 * Journal entry sync enqueue helpers.
 *
 * The journal module is local-first: writes land in IndexedDB immediately.
 * These helpers additionally enqueue the change onto the shared
 * {@link db.syncQueue} so the {@link ExtendedSyncQueue} (wired via
 * useProductivitySync) can drain it to the backend.
 *
 * Important routing detail: the journal backend has NO `POST /entries` — create
 * and update are both an upsert via `PUT /entries/{date}`. The ExtendedSyncQueue
 * maps action `create` → POST and `update` → PUT. So journal changes are ALWAYS
 * enqueued with action `update` (never `create`), which produces
 * `PUT /entries/{entryDate}`. Deletes use action `delete` → `DELETE /entries/{date}`.
 *
 * @module entrySync
 * @see Requirements 5.4, 8.1, 8.4, 12.6
 */

import type { ExtendedSyncQueueItem, SyncQueueItem } from '../types';
import { db } from './localDb';

/** Maximum number of items allowed in the sync queue across all modules. */
const MAX_SYNC_QUEUE_SIZE = 1000;

/** The payload the entry-crud PUT handler expects. */
export interface EntrySyncPayload {
  content: string;
  version: number;
  mediaRefs: string[];
}

/**
 * Remove any existing queued `journal` + entryDate + `update` items so the
 * debounced autosave does not grow the queue unboundedly. The latest content
 * wins (collapse to a single pending update per date).
 */
async function collapseExistingUpdates(entryDate: string): Promise<void> {
  const all = await db.syncQueue.toArray();
  const stale = all.filter((raw) => {
    const item = raw as unknown as ExtendedSyncQueueItem & { id?: number };
    return (
      item.module === 'journal' &&
      item.entityId === entryDate &&
      item.action === 'update'
    );
  });

  for (const raw of stale) {
    const id = (raw as unknown as { id?: number }).id;
    if (id != null) {
      await db.syncQueue.delete(id);
    }
  }
}

/**
 * Enqueue a journal create/update as an `update` action (→ PUT /entries/{date}).
 *
 * Dedupes by collapsing any existing queued update for the same date to the
 * latest. Respects the 1000-item queue cap; if the queue is full the local
 * write is NOT blocked (we simply skip enqueueing).
 */
export async function enqueueEntrySync(
  entryDate: string,
  payload: EntrySyncPayload
): Promise<void> {
  try {
    await collapseExistingUpdates(entryDate);

    const size = await db.syncQueue.count();
    if (size >= MAX_SYNC_QUEUE_SIZE) {
      // Queue full — do not block the local-first write.
      return;
    }

    await db.syncQueue.add({
      module: 'journal',
      entityId: entryDate,
      action: 'update',
      timestamp: new Date().toISOString(),
      payload: { ...payload },
      retryCount: 0,
    } as unknown as SyncQueueItem);
  } catch {
    // Never let a queue error break the local write.
  }
}

/**
 * Enqueue a journal delete (→ DELETE /entries/{date}).
 *
 * Also removes any pending `update` for the same date (no point syncing an
 * update for an entry we're deleting). Respects the queue cap.
 */
export async function enqueueEntryDelete(entryDate: string): Promise<void> {
  try {
    await collapseExistingUpdates(entryDate);

    const size = await db.syncQueue.count();
    if (size >= MAX_SYNC_QUEUE_SIZE) {
      return;
    }

    await db.syncQueue.add({
      module: 'journal',
      entityId: entryDate,
      action: 'delete',
      timestamp: new Date().toISOString(),
      payload: {},
      retryCount: 0,
    } as unknown as SyncQueueItem);
  } catch {
    // Never let a queue error break the local delete.
  }
}
